import { Injectable } from '@angular/core';

export interface AnamneseCacheData {
  anamneseId: string;
  formData: { [key: string]: any };
  timestamp: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class AnamneseCacheService {
  private readonly CACHE_KEY_PREFIX = 'anamnese_cache_';
  private readonly CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 horas em milissegundos

  constructor() {
    // Limpar caches expirados na inicialização do serviço
    this.cleanExpiredCaches();
  }

  /**
   * Salva os dados do formulário no cache local
   * @param anamneseId ID da anamnese
   * @param formData Dados do formulário para cachear
   */
  saveToCache(anamneseId: string, formData: { [key: string]: any }): void {
    try {
      if (!anamneseId) {
        console.warn('[AnamneseCacheService] ID da anamnese não fornecido para cache');
        return;
      }

      const now = Date.now();
      const cacheData: AnamneseCacheData = {
        anamneseId,
        formData: this.sanitizeFormData(formData),
        timestamp: now,
        expiresAt: now + this.CACHE_DURATION_MS
      };

      const cacheKey = this.getCacheKey(anamneseId);
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      
    } catch (error) {
      console.error('[AnamneseCacheService] Erro ao salvar cache:', error);
    }
  }

  /**
   * Recupera os dados do formulário do cache local
   * @param anamneseId ID da anamnese
   * @returns Dados do formulário ou null se não encontrado/expirado
   */
  getFromCache(anamneseId: string): { [key: string]: any } | null {
    try {
      if (!anamneseId) {
        console.warn('[AnamneseCacheService] ID da anamnese não fornecido para recuperação do cache');
        return null;
      }

      const cacheKey = this.getCacheKey(anamneseId);
      const cachedDataString = localStorage.getItem(cacheKey);
      
      if (!cachedDataString) {
        return null;
      }

      const cachedData: AnamneseCacheData = JSON.parse(cachedDataString);
      
      // Verificar se o cache expirou
      if (Date.now() > cachedData.expiresAt) {
        this.clearCache(anamneseId);
        return null;
      }

      return cachedData.formData;
    } catch (error) {
      console.error('[AnamneseCacheService] Erro ao recuperar cache:', error);
      return null;
    }
  }

  /**
   * Remove o cache para uma anamnese específica
   * @param anamneseId ID da anamnese
   */
  clearCache(anamneseId: string): void {
    try {
      if (!anamneseId) {
        console.warn('[AnamneseCacheService] ID da anamnese não fornecido para limpeza do cache');
        return;
      }

      const cacheKey = this.getCacheKey(anamneseId);
      localStorage.removeItem(cacheKey);
      
    } catch (error) {
      console.error('[AnamneseCacheService] Erro ao limpar cache:', error);
    }
  }

  /**
   * Verifica se existe cache válido para uma anamnese
   * @param anamneseId ID da anamnese
   * @returns true se existe cache válido, false caso contrário
   */
  hasValidCache(anamneseId: string): boolean {
    try {
      if (!anamneseId) {
        return false;
      }

      const cacheKey = this.getCacheKey(anamneseId);
      const cachedDataString = localStorage.getItem(cacheKey);
      
      if (!cachedDataString) {
        return false;
      }

      const cachedData: AnamneseCacheData = JSON.parse(cachedDataString);
      return Date.now() <= cachedData.expiresAt;
    } catch (error) {
      console.error('[AnamneseCacheService] Erro ao verificar cache:', error);
      return false;
    }
  }

  /**
   * Limpa todos os caches expirados
   */
  cleanExpiredCaches(): void {
    try {
      const now = Date.now();
      const keysToRemove: string[] = [];

      // Percorrer todas as chaves do localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          try {
            const cachedDataString = localStorage.getItem(key);
            if (cachedDataString) {
              const cachedData: AnamneseCacheData = JSON.parse(cachedDataString);
              if (now > cachedData.expiresAt) {
                keysToRemove.push(key);
              }
            }
          } catch (error) {
            // Se não conseguir parsear, remover a chave corrompida
            keysToRemove.push(key);
          }
        }
      }

      // Remover chaves expiradas ou corrompidas
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      if (keysToRemove.length > 0) {
      }
    } catch (error) {
      console.error('[AnamneseCacheService] Erro ao limpar caches expirados:', error);
    }
  }

  /**
   * Remove todos os caches de anamnese (útil para limpeza geral)
   */
  clearAllCaches(): void {
    try {
      const keysToRemove: string[] = [];

      // Encontrar todas as chaves de cache de anamnese
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.CACHE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      // Remover todas as chaves
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
    } catch (error) {
      console.error('[AnamneseCacheService] Erro ao limpar todos os caches:', error);
    }
  }

  /**
   * Obtém informações sobre o cache de uma anamnese
   * @param anamneseId ID da anamnese
   * @returns Informações sobre o cache ou null se não existir
   */
  getCacheInfo(anamneseId: string): { timestamp: number; expiresAt: number; timeUntilExpiry: number } | null {
    try {
      if (!anamneseId) {
        return null;
      }

      const cacheKey = this.getCacheKey(anamneseId);
      const cachedDataString = localStorage.getItem(cacheKey);
      
      if (!cachedDataString) {
        return null;
      }

      const cachedData: AnamneseCacheData = JSON.parse(cachedDataString);
      const now = Date.now();
      
      return {
        timestamp: cachedData.timestamp,
        expiresAt: cachedData.expiresAt,
        timeUntilExpiry: Math.max(0, cachedData.expiresAt - now)
      };
    } catch (error) {
      console.error('[AnamneseCacheService] Erro ao obter informações do cache:', error);
      return null;
    }
  }

  /**
   * Gera a chave do cache para uma anamnese
   * @param anamneseId ID da anamnese
   * @returns Chave do cache
   */
  private getCacheKey(anamneseId: string): string {
    return `${this.CACHE_KEY_PREFIX}${anamneseId}`;
  }

  /**
   * Sanitiza os dados do formulário antes de salvar no cache
   * Remove valores nulos, undefined e funções que não podem ser serializados
   * @param formData Dados do formulário
   * @returns Dados sanitizados
   */
  private sanitizeFormData(formData: { [key: string]: any }): { [key: string]: any } {
    const sanitized: { [key: string]: any } = {};

    for (const [key, value] of Object.entries(formData)) {
      // Pular valores que não podem ser serializados ou são inúteis
      if (value === null || value === undefined || typeof value === 'function') {
        continue;
      }

      // Para objetos, fazer uma sanitização recursiva
      if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        const sanitizedObject = this.sanitizeFormData(value);
        if (Object.keys(sanitizedObject).length > 0) {
          sanitized[key] = sanitizedObject;
        }
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }
}
